import React, { useState } from "react";
import { useSmartFilter } from "../hooks/useSmartFilter";

const SmartFilterPanel = () => {
  const {
    filterOptions,
    filterMetadata,
    quickFilters,
    globalSearch,
    setGlobalSearch,
    updateBooleanFilter,
    updateDropdownFilter,
    updateStringFilter,
    updateNumberFilter,
    updateDateFilter,
    clearFilterType,
    filterPresets,
    applyFilterPreset,
    saveFilterPreset,
    hasActiveFilters,
    clearAllFilters,
    adminNames,
  } = useSmartFilter();

  const [activeTab, setActiveTab] = useState("boolean");
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState("");

  const tabs = [
    { id: "boolean", label: "Yes/No Fields", icon: "✓" },
    { id: "dropdown", label: "Categories", icon: "▼" },
    { id: "string", label: "Text Search", icon: "🔍" },
    { id: "number", label: "Number Range", icon: "#" },
    { id: "date", label: "Date Range", icon: "📅" },
    { id: "admin", label: "Admin Filters", icon: "👤" },
    { id: "presets", label: "Saved Filters", icon: "⭐" },
  ];

  const handleSavePreset = async () => {
    if (presetName.trim()) {
      try {
        await saveFilterPreset(
          presetName,
          `Custom filter: ${presetName}`,
          false
        );
        setPresetName("");
        setShowSavePreset(false);
      } catch (error) {}
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border">
      {/* Global Search */}
      <div className="p-4 border-b">
        <div className="flex items-center space-x-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search across all fields..."
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => setShowSavePreset(!showSavePreset)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Save Filter
          </button>
        </div>

        {showSavePreset && (
          <div className="mt-3 flex items-center space-x-2">
            <input
              type="text"
              placeholder="Filter name..."
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
            />
            <button
              onClick={handleSavePreset}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Save
            </button>
            <button
              onClick={() => setShowSavePreset(false)}
              className="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-500"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex border-b">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-3 text-sm font-medium ${
              activeTab === tab.id
                ? "bg-blue-50 text-blue-700 border-b-2 border-blue-500"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filter Content */}
      <div className="p-4">
        {/* Boolean Filters */}
        {activeTab === "boolean" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filterMetadata.booleanFields?.map((field) => (
              <div key={field.field} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {field.label}
                </label>
                <select
                  value={quickFilters.booleanFilters[field.field] || "all"}
                  onChange={(e) =>
                    updateBooleanFilter(field.field, e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            ))}
          </div>
        )}

        {/* Dropdown Filters */}
        {activeTab === "dropdown" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filterMetadata.dropdownFields?.map((field) => (
              <div key={field.field} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {field.label}
                </label>
                <select
                  multiple
                  value={quickFilters.dropdownFilters[field.field] || []}
                  onChange={(e) => {
                    const values = Array.from(
                      e.target.selectedOptions,
                      (option) => option.value
                    );
                    updateDropdownFilter(field.field, values);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-32"
                >
                  {filterOptions[field.field]?.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">
                  Hold Ctrl/Cmd to select multiple
                </p>
              </div>
            ))}
          </div>
        )}

        {/* String Filters */}
        {activeTab === "string" && (
          <div className="space-y-4">
            {filterMetadata.stringFields?.map((field) => (
              <div key={field.field} className="flex items-center space-x-3">
                <div className="w-1/4">
                  <label className="block text-sm font-medium text-gray-700">
                    {field.label}
                  </label>
                </div>
                <div className="w-1/4">
                  <select
                    value={
                      quickFilters.stringFilters[field.field]?.operator ||
                      "contains"
                    }
                    onChange={(e) => {
                      const value =
                        quickFilters.stringFilters[field.field]?.value || "";
                      updateStringFilter(field.field, value, e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="contains">Contains</option>
                    <option value="starts_with">Starts with</option>
                    <option value="ends_with">Ends with</option>
                    <option value="equals">Exact match</option>
                  </select>
                </div>
                <div className="flex-1">
                  <input
                    type="text"
                    value={quickFilters.stringFilters[field.field]?.value || ""}
                    onChange={(e) => {
                      const operator =
                        quickFilters.stringFilters[field.field]?.operator ||
                        "contains";
                      updateStringFilter(field.field, e.target.value, operator);
                    }}
                    placeholder={`Search ${field.label.toLowerCase()}...`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Number Filters */}
        {activeTab === "number" && (
          <div className="space-y-4">
            {filterMetadata.numberFields?.map((field) => (
              <div key={field.field} className="flex items-center space-x-3">
                <div className="w-1/3">
                  <label className="block text-sm font-medium text-gray-700">
                    {field.label}
                  </label>
                </div>
                <div className="w-1/3">
                  <input
                    type="number"
                    placeholder="Min"
                    value={quickFilters.numberFilters[field.field]?.min || ""}
                    onChange={(e) => {
                      const max = quickFilters.numberFilters[field.field]?.max;
                      updateNumberFilter(field.field, e.target.value, max);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="w-1/3">
                  <input
                    type="number"
                    placeholder="Max"
                    value={quickFilters.numberFilters[field.field]?.max || ""}
                    onChange={(e) => {
                      const min = quickFilters.numberFilters[field.field]?.min;
                      updateNumberFilter(field.field, min, e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Date Filters */}
        {activeTab === "date" && (
          <div className="space-y-4">
            {filterMetadata.dateFields?.map((field) => (
              <div key={field.field} className="flex items-center space-x-3">
                <div className="w-1/3">
                  <label className="block text-sm font-medium text-gray-700">
                    {field.label}
                  </label>
                </div>
                <div className="w-1/3">
                  <input
                    type="date"
                    value={
                      quickFilters.dateFilters[field.field]?.startDate || ""
                    }
                    onChange={(e) => {
                      const endDate =
                        quickFilters.dateFilters[field.field]?.endDate;
                      updateDateFilter(field.field, e.target.value, endDate);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="w-1/3">
                  <input
                    type="date"
                    value={quickFilters.dateFilters[field.field]?.endDate || ""}
                    onChange={(e) => {
                      const startDate =
                        quickFilters.dateFilters[field.field]?.startDate;
                      updateDateFilter(field.field, startDate, e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Admin Filters */}
        {activeTab === "admin" && (
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-1/3">
                <label className="block text-sm font-medium text-gray-700">
                  Contacted By
                </label>
              </div>
              <div className="w-2/3">
                <select
                  multiple
                  value={quickFilters.dropdownFilters["contactedBy"] || []}
                  onChange={(e) => {
                    const values = Array.from(
                      e.target.selectedOptions,
                      (option) => option.value
                    );
                    updateDropdownFilter("contactedBy", values);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-32"
                >
                  {adminNames?.map((admin) => (
                    <option key={admin._id} value={admin._id}>
                      {admin.name} ({admin.email})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Hold Ctrl/Cmd to select multiple admins
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-1/3">
                <label className="block text-sm font-medium text-gray-700">
                  Deal Status
                </label>
              </div>
              <div className="w-2/3">
                <select
                  multiple
                  value={quickFilters.dropdownFilters["dealStatus"] || []}
                  onChange={(e) => {
                    const values = Array.from(
                      e.target.selectedOptions,
                      (option) => option.value
                    );
                    updateDropdownFilter("dealStatus", values);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
                >
                  <option value="open">Open</option>
                  <option value="contacted">Contacted</option>
                  <option value="interested">Interested</option>
                  <option value="quoted">Quoted</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                  <option value="closed won">Closed Won</option>
                  <option value="closed lost">Closed Lost</option>
                  <option value="no response">No Response</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Hold Ctrl/Cmd to select multiple statuses
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Saved Presets */}
        {activeTab === "presets" && (
          <div className="space-y-3">
            {filterPresets.length > 0 ? (
              filterPresets.map((preset) => (
                <div
                  key={preset._id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div>
                    <h4 className="font-medium">{preset.name}</h4>
                    <p className="text-sm text-gray-500">
                      {preset.description}
                    </p>
                  </div>
                  <button
                    onClick={() => applyFilterPreset(preset)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Apply
                  </button>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No saved filter presets found.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Clear Buttons */}
      <div className="px-4 py-3 bg-gray-50 border-t">
        <div className="flex justify-between">
          <button
            onClick={() =>
              clearFilterType(
                activeTab === "boolean"
                  ? "booleanFilters"
                  : activeTab === "dropdown"
                  ? "dropdownFilters"
                  : activeTab === "string"
                  ? "stringFilters"
                  : activeTab === "number"
                  ? "numberFilters"
                  : activeTab === "admin"
                  ? "dropdownFilters" // Admin filters use dropdown filter type
                  : "dateFilters"
              )
            }
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Clear {tabs.find((t) => t.id === activeTab)?.label}
          </button>
          {hasActiveFilters() && (
            <button
              onClick={clearAllFilters}
              className="px-4 py-2 text-sm bg-red-200 text-red-700 rounded-md hover:bg-red-300"
            >
              Clear All Filters
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartFilterPanel;
